export const DEFAULT_PHONE_COUNTRY_CODE = "+1";

/** Common dial codes for the Business Manager phone field. */
export const PHONE_COUNTRY_CODES: Array<{
	dial: string;
	iso: string;
	label: string;
}> = [
	{ dial: "+1", iso: "US", label: "United States (+1)" },
	{ dial: "+91", iso: "IN", label: "India (+91)" },
	{ dial: "+44", iso: "GB", label: "United Kingdom (+44)" },
	{ dial: "+61", iso: "AU", label: "Australia (+61)" },
	{ dial: "+971", iso: "AE", label: "United Arab Emirates (+971)" },
	{ dial: "+65", iso: "SG", label: "Singapore (+65)" },
	{ dial: "+60", iso: "MY", label: "Malaysia (+60)" },
	{ dial: "+62", iso: "ID", label: "Indonesia (+62)" },
	{ dial: "+63", iso: "PH", label: "Philippines (+63)" },
	{ dial: "+66", iso: "TH", label: "Thailand (+66)" },
	{ dial: "+81", iso: "JP", label: "Japan (+81)" },
	{ dial: "+82", iso: "KR", label: "South Korea (+82)" },
	{ dial: "+86", iso: "CN", label: "China (+86)" },
	{ dial: "+49", iso: "DE", label: "Germany (+49)" },
	{ dial: "+33", iso: "FR", label: "France (+33)" },
	{ dial: "+39", iso: "IT", label: "Italy (+39)" },
	{ dial: "+34", iso: "ES", label: "Spain (+34)" },
	{ dial: "+31", iso: "NL", label: "Netherlands (+31)" },
	{ dial: "+46", iso: "SE", label: "Sweden (+46)" },
	{ dial: "+47", iso: "NO", label: "Norway (+47)" },
	{ dial: "+41", iso: "CH", label: "Switzerland (+41)" },
	{ dial: "+353", iso: "IE", label: "Ireland (+353)" },
	{ dial: "+64", iso: "NZ", label: "New Zealand (+64)" },
	{ dial: "+27", iso: "ZA", label: "South Africa (+27)" },
	{ dial: "+55", iso: "BR", label: "Brazil (+55)" },
	{ dial: "+52", iso: "MX", label: "Mexico (+52)" },
	{ dial: "+54", iso: "AR", label: "Argentina (+54)" },
	{ dial: "+92", iso: "PK", label: "Pakistan (+92)" },
	{ dial: "+880", iso: "BD", label: "Bangladesh (+880)" },
	{ dial: "+94", iso: "LK", label: "Sri Lanka (+94)" },
	{ dial: "+977", iso: "NP", label: "Nepal (+977)" },
	{ dial: "+966", iso: "SA", label: "Saudi Arabia (+966)" },
	{ dial: "+974", iso: "QA", label: "Qatar (+974)" },
	{ dial: "+965", iso: "KW", label: "Kuwait (+965)" },
];

/** Longest-prefix match so +971 wins over +9… */
const DIAL_CODES_LONGEST_FIRST = [...PHONE_COUNTRY_CODES].sort(
	(a, b) => b.dial.length - a.dial.length,
);

const REGION_TO_DIAL: Record<string, string> = {
	IN: "+91",
	INDIA: "+91",
	US: "+1",
	USA: "+1",
	"UNITED STATES": "+1",
	CA: "+1",
	GB: "+44",
	UK: "+44",
	AU: "+61",
	AE: "+971",
	SG: "+65",
	MY: "+60",
	ID: "+62",
	PH: "+63",
	TH: "+66",
	JP: "+81",
	KR: "+82",
	CN: "+86",
	DE: "+49",
	FR: "+33",
	IT: "+39",
	ES: "+34",
	NL: "+31",
	IE: "+353",
	NZ: "+64",
	ZA: "+27",
	BR: "+55",
	MX: "+52",
	PK: "+92",
	BD: "+880",
	LK: "+94",
	NP: "+977",
	SA: "+966",
	QA: "+974",
	KW: "+965",
};

function dialFromRegionHint(regionHint?: string | null): string | null {
	if (!regionHint?.trim()) return null;
	return REGION_TO_DIAL[regionHint.trim().toUpperCase()] ?? null;
}

/** Strip trunk 0 for display in the national field. */
function stripTrunkZero(national: string): string {
	return national.replace(/\D/g, "").replace(/^0+/, "");
}

/**
 * Split stored E.164 / local phone into country dropdown + national digits.
 * Infers +91 for Indian mobiles and uses address region when dial is missing.
 */
export function splitPhoneNumber(
	phone: string | null | undefined,
	fallbackDial = DEFAULT_PHONE_COUNTRY_CODE,
	regionHint?: string | null,
): { phoneCountryCode: string; phoneNational: string } {
	const regionDial = dialFromRegionHint(regionHint);
	const defaultDial = regionDial || fallbackDial;

	if (!phone?.trim()) {
		return { phoneCountryCode: defaultDial, phoneNational: "" };
	}

	const trimmed = phone.trim();

	if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
		const withPlus = trimmed.startsWith("00")
			? `+${trimmed.slice(2)}`
			: trimmed;
		const match = DIAL_CODES_LONGEST_FIRST.find((c) =>
			withPlus.startsWith(c.dial),
		);
		if (match) {
			const rest = withPlus.slice(match.dial.length).trim();
			const national = stripTrunkZero(rest);
			return {
				phoneCountryCode: match.dial,
				phoneNational: national,
			};
		}
		// +0XXXXXXXX (plus + trunk, no real country) → treat as national
		const digits = withPlus.replace(/\D/g, "").replace(/^0+/, "");
		if (/^[6-9]\d{9}$/.test(digits)) {
			return { phoneCountryCode: "+91", phoneNational: digits };
		}
		return {
			phoneCountryCode: defaultDial,
			phoneNational: digits,
		};
	}

	// Local / national format (may include trunk 0)
	const digits = trimmed.replace(/\D/g, "");
	if (/^0[6-9]\d{9}$/.test(digits)) {
		return {
			phoneCountryCode: regionDial || "+91",
			phoneNational: digits.slice(1),
		};
	}
	if (/^[6-9]\d{9}$/.test(digits)) {
		return {
			phoneCountryCode: regionDial || "+91",
			phoneNational: digits,
		};
	}

	const national = stripTrunkZero(digits);
	return {
		phoneCountryCode: defaultDial,
		phoneNational: national,
	};
}

/** Build E.164-ish `+<dial><national digits>` for GBP / Facebook. */
export function composePhoneNumber(
	countryCode: string | null | undefined,
	national: string | null | undefined,
): string | undefined {
	const raw = (national ?? "").trim();
	// If the national field already has a full international number, keep it.
	if (raw.startsWith("+") && raw.replace(/\D/g, "").length >= 10) {
		let digits = raw.replace(/\D/g, "").replace(/^0+/, "");
		if (/^[6-9]\d{9}$/.test(digits)) {
			digits = `91${digits}`;
		}
		if (digits.length < 10 || digits.length > 15) {
			return undefined;
		}
		return `+${digits}`;
	}

	const dial = (countryCode ?? "").trim() || DEFAULT_PHONE_COUNTRY_CODE;
	// Drop a leading trunk 0 (e.g. 099259… with +91 → +9199259…).
	const digits = raw.replace(/\D/g, "").replace(/^0+/, "");
	if (!digits) {
		return undefined;
	}
	const dialDigits = dial.replace(/\D/g, "");
	if (!dialDigits) {
		return undefined;
	}
	return `+${dialDigits}${digits}`;
}
